/*
 * <promo-video> custom element
 *
 * - Hovering a card enlarges the play button (CSS-only — see promo-video.css).
 *   No autoplay preview, no black overlay; the cover image stays visible.
 * - Click on a card opens a full-viewport lightbox (50% dim on the page)
 *   with the same source rebuilt with sound + controls.
 * - Lightbox closes on ESC, click anywhere outside the player, or close button.
 *
 * Triggers: any descendant carrying [data-promo-video-trigger] with
 *   - data-promo-video-type="youtube" + data-promo-video-id="<id>"
 *   - data-promo-video-type="shopify" + data-promo-video-sources='[{...}]'
 */

const TRANSITION_MS = 250;

class PromoVideo extends HTMLElement {
  constructor() {
    super();
    this.lightbox = null;
    this.stage = null;
    this.lastTrigger = null;
    this._closeTimer = null;
    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onLightboxClick = this._onLightboxClick.bind(this);
  }

  connectedCallback() {
    if (!this.lightbox) {
      this.lightbox = this.querySelector('[data-promo-video-lightbox]');
    }
    this.stage = this.lightbox ? this.lightbox.querySelector('[data-promo-video-stage]') : null;

    // Portal the lightbox to <body> so position:fixed resolves against the viewport.
    // Ancestors with `transform`, `filter`, `perspective`, `contain`, or `will-change`
    // become the containing block for fixed descendants — e.g. <motion-element style="transform: ..."> in the product info column would otherwise clip our lightbox to the column's box.
    if (this.lightbox && this.lightbox.parentNode !== document.body) {
      document.body.appendChild(this.lightbox);
    }

    if (this.lightbox) {
      this.lightbox.removeAttribute('hidden');
      this.lightbox.setAttribute('data-state', 'closed');
    }

    this.addEventListener('click', this._onClick);
    if (this.lightbox) this.lightbox.addEventListener('click', this._onLightboxClick);
  }

  disconnectedCallback() {
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
    this._teardownPlayer();
    this.removeEventListener('click', this._onClick);
    if (this.lightbox) {
      this.lightbox.removeEventListener('click', this._onLightboxClick);
      if (this.lightbox.parentNode === document.body) {
        this.lightbox.remove();
      }
    }
    if (this._previousBodyOverflow !== undefined) {
      document.body.style.overflow = this._previousBodyOverflow;
      this._previousBodyOverflow = undefined;
    }
    if (this._previousBodyPaddingRight !== undefined) {
      document.body.style.paddingRight = this._previousBodyPaddingRight;
      this._previousBodyPaddingRight = undefined;
    }
    document.removeEventListener('keydown', this._onKeyDown);
  }

  _onClick(event) {
    // Triggers (cards) live inside <promo-video>. The close button and backdrop
    // live on the portaled lightbox (now in <body>) — they're handled in _onLightboxClick.
    const trigger = event.target.closest('[data-promo-video-trigger]');
    if (trigger && this.contains(trigger)) {
      event.preventDefault();
      this.open(trigger);
    }
  }

  _onLightboxClick(event) {
    const closeBtn = event.target.closest('[data-promo-video-close]');
    if (closeBtn) {
      event.preventDefault();
      this.close();
      return;
    }
    // Close on any click outside the actual video player. Stage padding,
    // backdrop, and surrounding bleed all dismiss; only the player itself
    // (iframe / <video>) keeps the lightbox open so user interactions with
    // controls aren't intercepted.
    if (!event.target.closest('.promo-video__player')) {
      this.close();
    }
  }

  _onKeyDown(event) {
    if (event.key === 'Escape') this.close();
  }

  open(trigger) {
    if (!this.lightbox || !this.stage) return;

    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }

    this.lastTrigger = trigger;
    this._teardownPlayer();

    const type = trigger.dataset.promoVideoType;
    if (type === 'youtube') {
      const id = (trigger.dataset.promoVideoId || '').trim();
      if (!id) return;
      const iframe = document.createElement('iframe');
      const params = new URLSearchParams({
        autoplay: '1',
        rel: '0',
        playsinline: '1',
        modestbranding: '1',
      });
      iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(id)}?${params.toString()}`;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.setAttribute('allowfullscreen', '');
      iframe.title = 'YouTube video player';
      iframe.className = 'promo-video__player promo-video__player--youtube';
      this.stage.appendChild(iframe);
    } else if (type === 'shopify') {
      // Clone the server-rendered <video> from the card's <template>. Shopify's
      // `video_tag` Liquid filter built it with the correct sources (mp4 + HLS
      // ordering, CDN URLs, etc.). Building the <video> in JS via JSON-parsed
      // sources broke for some Shopify-hosted uploads — letting Liquid emit the
      // canonical markup and just cloning it is bulletproof.
      const tpl = trigger.querySelector('[data-promo-video-template]');
      if (!tpl || !tpl.content) return;
      const fragment = tpl.content.cloneNode(true);
      const video = fragment.querySelector('video');
      if (!video) return;
      video.classList.add('promo-video__player', 'promo-video__player--shopify');
      // Force the playback flags in case the server omitted any (defensive).
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      this.stage.appendChild(fragment);
      // Auto-resolved captions for the lightbox <video> (see assets/video-captions.js).
      // No-op when the helper is absent or the trigger carries no data-subtitle-url.
      if (window.LAGVideoCaptions) window.LAGVideoCaptions.attach(video, trigger.dataset.subtitleUrl);
    } else {
      return;
    }

    const dialogLabel = (trigger.getAttribute('aria-label') || '').trim();
    if (dialogLabel) this.lightbox.setAttribute('aria-label', dialogLabel);
    this.lightbox.setAttribute('aria-hidden', 'false');

    // Lock body scroll without causing layout shift. Setting overflow:hidden
    // on body removes the vertical scrollbar; on desktop browsers with classic
    // (non-overlay) scrollbars that frees ~15-17px of viewport width and the
    // page reflows horizontally — which the user perceives as a jolt
    // (motion-element transforms and sticky ancestors re-evaluate during the
    // reflow). Adding padding-right of the same width keeps the content area
    // dimensionally identical, so nothing reflows.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    this._previousBodyOverflow = document.body.style.overflow;
    this._previousBodyPaddingRight = document.body.style.paddingRight;
    if (scrollbarWidth > 0) {
      const currentPad = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${currentPad + scrollbarWidth}px`;
    }
    document.body.style.overflow = 'hidden';

    // Force layout flush before flipping data-state so the opacity transition
    // runs from 0 -> 1 instead of snapping straight to open.
    void this.lightbox.offsetWidth;
    this.lightbox.setAttribute('data-state', 'open');

    document.addEventListener('keydown', this._onKeyDown);

    const closeBtn = this.lightbox.querySelector('[data-promo-video-close]');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  close() {
    if (!this.lightbox) return;
    if (this.lightbox.getAttribute('data-state') !== 'open') return;

    this.lightbox.setAttribute('data-state', 'closed');
    this.lightbox.setAttribute('aria-hidden', 'true');

    document.removeEventListener('keydown', this._onKeyDown);

    if (this._previousBodyOverflow !== undefined) {
      document.body.style.overflow = this._previousBodyOverflow;
      this._previousBodyOverflow = undefined;
    }
    if (this._previousBodyPaddingRight !== undefined) {
      document.body.style.paddingRight = this._previousBodyPaddingRight;
      this._previousBodyPaddingRight = undefined;
    }

    // Defer player teardown until the fade-out has finished so the audio
    // doesn't cut on frame 1 and so the user sees the player fade with the bg.
    if (this._closeTimer) clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => {
      this._teardownPlayer();
      this._closeTimer = null;
    }, TRANSITION_MS);

    if (this.lastTrigger) {
      try { this.lastTrigger.focus({ preventScroll: true }); } catch (_err) { /* ignore */ }
      this.lastTrigger = null;
    }
  }

  _teardownPlayer() {
    if (!this.stage) return;
    const video = this.stage.querySelector('video');
    if (video) {
      try { video.pause(); } catch (_err) { /* ignore */ }
      video.removeAttribute('src');
      while (video.firstChild) video.removeChild(video.firstChild);
      try { video.load(); } catch (_err) { /* ignore */ }
    }
    this.stage.replaceChildren();
  }
}

if (!customElements.get('promo-video')) {
  customElements.define('promo-video', PromoVideo);
}

/*
 * <promo-video-list> — scrollable card row.
 * Drag-to-scroll + vertical-wheel passthrough, ported verbatim from
 * assets/product-tabs-v2.js → ProductTabsCarousel so the two carousels
 * behave identically.
 */
if (!customElements.get('promo-video-list')) {
  class PromoVideoList extends HTMLElement {
    connectedCallback() {
      this.bindDrag();
      this.bindWheelRedirect();
    }

    bindWheelRedirect() {
      this.addEventListener(
        'wheel',
        (e) => {
          const absX = Math.abs(e.deltaX);
          const absY = Math.abs(e.deltaY);
          if (absY > absX) {
            e.preventDefault();
            window.scrollBy({ top: e.deltaY, left: 0, behavior: 'auto' });
          }
        },
        { passive: false }
      );
    }

    bindDrag() {
      let isDown = false;
      let startX = 0;
      let startScrollLeft = 0;
      let pointerId = null;
      const DRAG_THRESHOLD = 4;
      let dragged = false;

      const onDown = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        if (e.target.closest('a, button, input, select, textarea, [role="button"]')) return;
        isDown = true;
        dragged = false;
        pointerId = e.pointerId;
        startX = e.clientX;
        startScrollLeft = this.scrollLeft;
        try { this.setPointerCapture(pointerId); } catch (_) {}
      };

      const onMove = (e) => {
        if (!isDown) return;
        const dx = e.clientX - startX;
        if (!dragged && Math.abs(dx) > DRAG_THRESHOLD) {
          dragged = true;
          this.classList.add('is-dragging');
        }
        if (dragged) {
          this.scrollLeft = startScrollLeft - dx;
          e.preventDefault();
        }
      };

      const onUp = () => {
        if (!isDown) return;
        try { this.releasePointerCapture(pointerId); } catch (_) {}
        isDown = false;
        if (dragged) {
          this.classList.remove('is-dragging');
          const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          this.addEventListener('click', swallow, { capture: true, once: true });
        }
        dragged = false;
        pointerId = null;
      };

      this.addEventListener('pointerdown', onDown);
      this.addEventListener('pointermove', onMove);
      this.addEventListener('pointerup', onUp);
      this.addEventListener('pointercancel', onUp);
    }
  }
  customElements.define('promo-video-list', PromoVideoList);
}

/*
 * <promo-video-scrollbar> — track + thumb + prev/next arrows tied to the
 * preceding <promo-video-list>. Adapted from product-tabs-scrollbar; same
 * thumb sizing, drag, arrow scroll-by-85% behavior. Self-hides when the
 * list does not overflow.
 */
if (!customElements.get('promo-video-scrollbar')) {
  class PromoVideoScrollbar extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.promo-video__bar');
      this.thumb = this.querySelector('.promo-video__thumb');
      this.prevBtn = this.querySelector('.promo-video__arrow--prev');
      this.nextBtn = this.querySelector('.promo-video__arrow--next');
      if (!this.bar || !this.thumb || !this.prevBtn || !this.nextBtn) return;

      const host = this.closest('promo-video');
      this.list = host ? host.querySelector('promo-video-list') : null;
      if (!this.list) return;

      this.onScroll = () => this.update();
      this.onResize = () => this.update();

      this.prevBtn.addEventListener('click', () => this.scroll(-0.85));
      this.nextBtn.addEventListener('click', () => this.scroll(0.85));
      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.list);
      this.bindBarDrag();

      requestAnimationFrame(() => this.update());
    }

    disconnectedCallback() {
      if (this.list) this.list.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.resizeObserver?.disconnect();
    }

    update() {
      const el = this.list;
      if (!el) {
        this.classList.add('tw-hidden');
        return;
      }
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 1) {
        this.classList.add('tw-hidden');
        return;
      }
      this.classList.remove('tw-hidden');

      const visibleRatio = el.clientWidth / el.scrollWidth;
      const thumbWidth = Math.max(0.12, visibleRatio);
      const progress = el.scrollLeft / maxScroll;
      const thumbLeft = progress * (1 - thumbWidth);

      this.thumb.style.width = `${(thumbWidth * 100).toFixed(2)}%`;
      this.thumb.style.left = `${(thumbLeft * 100).toFixed(2)}%`;
      this.prevBtn.disabled = el.scrollLeft <= 1;
      this.nextBtn.disabled = el.scrollLeft >= maxScroll - 1;
    }

    scroll(ratio) {
      if (!this.list) return;
      this.list.scrollBy({
        left: this.list.clientWidth * ratio,
        behavior: 'smooth',
      });
    }

    bindBarDrag() {
      let dragging = false;
      let pointerId = null;

      const getProgress = (clientX) => {
        const rect = this.bar.getBoundingClientRect();
        return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      };

      const applyProgress = (progress) => {
        const el = this.list;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll <= 0) return;
        const visibleRatio = el.clientWidth / el.scrollWidth;
        const thumbWidth = Math.max(0.12, visibleRatio);
        const thumbLeft = Math.max(0, Math.min(1 - thumbWidth, progress - thumbWidth / 2));
        const scrollProgress = (1 - thumbWidth) > 0 ? thumbLeft / (1 - thumbWidth) : 0;
        el.scrollLeft = scrollProgress * maxScroll;
      };

      this.bar.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        dragging = true;
        pointerId = e.pointerId;
        try { this.bar.setPointerCapture(pointerId); } catch (_) {}
        this.thumb.style.transitionDuration = '0ms';
        applyProgress(getProgress(e.clientX));
      });

      this.bar.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        e.preventDefault();
        applyProgress(getProgress(e.clientX));
      });

      const onUp = () => {
        if (!dragging) return;
        dragging = false;
        try { this.bar.releasePointerCapture(pointerId); } catch (_) {}
        pointerId = null;
        this.thumb.style.transitionDuration = '';
      };

      this.bar.addEventListener('pointerup', onUp);
      this.bar.addEventListener('pointercancel', onUp);
    }
  }
  customElements.define('promo-video-scrollbar', PromoVideoScrollbar);
}

/*
 * Liquid-glass cursor glow on the product-info promo-video play button —
 * mirrors the slideshow-banner / video-v2 control. The play button is
 * pointer-events:none, so the pointer is tracked on the card (.promo-video__card)
 * and the specular gradient is positioned relative to the centered 44px button;
 * when the cursor is away from the button the gradient falls outside it and
 * reads as no glow. Scoped to .promo-video__card so the cc-v2 trigger is unaffected.
 */
(function () {
  function wireGlassGlow(card) {
    if (card.dataset.glassGlowWired) return;
    var play = card.querySelector('.promo-video__play');
    var spec = card.querySelector('.promo-video__play .vb-glass__specular');
    if (!play || !spec) return;
    card.dataset.glassGlowWired = '1';
    card.addEventListener('mousemove', function (e) {
      var r = play.getBoundingClientRect();
      var x = e.clientX - r.left;
      var y = e.clientY - r.top;
      spec.style.background =
        'radial-gradient(circle at ' + x + 'px ' + y + 'px, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 30%, rgba(255,255,255,0) 60%)';
    });
    card.addEventListener('mouseleave', function () {
      spec.style.background = '';
    });
  }
  function initGlassGlow(root) {
    var cards = (root || document).querySelectorAll('.promo-video__card');
    for (var i = 0; i < cards.length; i++) wireGlassGlow(cards[i]);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initGlassGlow(document);
    });
  } else {
    initGlassGlow(document);
  }
})();
